import { ReactRouterLink } from '@uds/react-router-adapter';
import { useTheme } from '@uds/theme-utils';
import createImageUrl from 'presentation/utils/createImageUrl';
import createImageAlt from 'presentation/utils/createImageAlt';
import { imageAspectRatioPercentage } from 'presentation/utils/imageAspectRatioPercentage';
import { useIsExternalRoute } from 'domain/selectors/common';
import { useCurrentLanguage } from 'domain/selectors/page';
import { ImageAspectRatio } from 'domain/constants/imageAspectRatio';
import { RefObject, useEffect, useRef, useState } from 'react';

import { getClassnames, StyledContent } from './CardImage.stylesheet';

export interface OuterProps {
  imageUrl: string | undefined;
  illustration: string | undefined;
  imageAspectRatio?: ImageAspectRatio;
  ctaUrl?: string;
}
// Hook
const useOnScreen = (ref: RefObject<HTMLDivElement>) => {
  const [isIntersecting, setIntersecting] = useState(false);
  useEffect(() => {
    const current = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update our state when observer callback fires
        setIntersecting(entry.isIntersecting);
      },
      {
        threshold: 0.9,
      },
    );
    if (current) {
      observer.observe(current);
    }
    return () => {
      // @ts-ignore ref not found.
      observer.unobserve(current);
    };
  }, [ref]);
  return isIntersecting;
};

const CardImage = ({ ctaUrl, imageAspectRatio, illustration, imageUrl }: OuterProps) => {
  const { theme } = useTheme();
  const currentLanguage = useCurrentLanguage();
  const [isExternalRoute, cardImageUrl] = useIsExternalRoute(ctaUrl);
  const assetUrl = createImageUrl(
    {
      illustration,
      imageUrl,
    },
    theme,
    currentLanguage,
  );

  const ratioPercentage = imageAspectRatioPercentage(imageAspectRatio);
  const alt = createImageAlt({ imageUrl });

  const classnames = getClassnames({
    hasIllustration: illustration != null,
  });
  // when not fully visible, let grey background in, otherwise, remove gray background
  // to prevent bleed
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(ref);
  return (
    <StyledContent percentage={ratioPercentage}>
      <div ref={ref} className={isVisible ? classnames.inner : classnames.innerWithBackground}>
        {cardImageUrl == null ? (
          <img loading="lazy" className={classnames.image} src={assetUrl} alt={alt} />
        ) : (
          <ReactRouterLink href={cardImageUrl} isExternalRoute={isExternalRoute}>
            <img loading="lazy" className={classnames.image} src={assetUrl} alt={alt} />
          </ReactRouterLink>
        )}
      </div>
    </StyledContent>
  );
};

CardImage.displayName = 'CardImage';
export default CardImage;
